import { useState } from "react";
import { useAdminSession } from "@/auth/AdminSession";
import { openTenant } from "@/auth/tenantSelection";
import ApiService from "@/services/ApiService";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Frame,
  Panel,
  Feedback,
  LoadState,
  Pager,
  Page,
  useData,
  useMutation,
  fieldClass,
  roles,
  statuses,
  japanDate,
} from "./ui";
const cell = "p-3 border-b align-top";
const today = () =>
  new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Tokyo" });

type Audit = {
  id: number;
  actor_id: number;
  tenant_type: string;
  tenant_id: number;
  action: string;
  result: string;
  reason: string;
  created_at: string;
  request_id: string;
  changes: unknown;
};
export function AuditPage({ platform = false }: { platform?: boolean }) {
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState({
    search: "",
    result: "",
    from: "",
    to: "",
  });
  const [filters, setFilters] = useState({});
  const result = useData<Page<Audit>>(
    platform ? "platform/audit-logs" : "organization/audit-logs",
    { ...filters, page },
  );
  return (
    <Frame
      title="監査ログ"
      description="操作した本人・対象組織・理由・処理結果を記録しています。日時は日本時間です。"
    >
      <Panel>
        <form
          className="flex flex-wrap gap-3 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            setFilters(
              Object.fromEntries(Object.entries(draft).filter(([, v]) => v)),
            );
            setPage(1);
          }}
        >
          <label>
            操作
            <Input
              value={draft.search}
              onChange={(e) => setDraft({ ...draft, search: e.target.value })}
            />
          </label>
          <label>
            結果
            <select
              className={fieldClass}
              value={draft.result}
              onChange={(e) => setDraft({ ...draft, result: e.target.value })}
            >
              <option value="">すべて</option>
              <option value="success">成功</option>
              <option value="failed">失敗</option>
              <option value="denied">拒否</option>
            </select>
          </label>
          <label>
            開始
            <Input
              type="date"
              value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
            />
          </label>
          <label>
            終了
            <Input
              type="date"
              value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
            />
          </label>
          <Button>検索</Button>
        </form>
        <LoadState {...result} />
        {result.data && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr>
                    {[
                      "日時 / 本人",
                      "対象組織",
                      "操作 / 結果",
                      "理由 / 詳細",
                    ].map((t) => (
                      <th className={cell} key={t}>
                        {t}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.data.data.map((r) => (
                    <tr key={r.id}>
                      <td className={cell}>
                        {japanDate(r.created_at)}
                        <p>ユーザー #{r.actor_id ?? "—"}</p>
                      </td>
                      <td className={cell}>
                        {r.tenant_type} #{r.tenant_id}
                      </td>
                      <td className={cell}>
                        {r.action}
                        <p>{r.result}</p>
                      </td>
                      <td className={cell}>
                        {r.reason || "—"}
                        <details>
                          <summary>詳細</summary>
                          <p className="break-all">{r.request_id}</p>
                          <pre className="whitespace-pre-wrap">
                            {typeof r.changes === "string"
                              ? r.changes
                              : JSON.stringify(r.changes)}
                          </pre>
                        </details>
                      </td>
                    </tr>
                  ))}
                  {!result.data.data.length && (
                    <tr>
                      <td colSpan={4} className={cell}>
                        該当するログはありません。
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pager result={result.data} setPage={setPage} />
          </>
        )}
      </Panel>
    </Frame>
  );
}
type Report = {
  applications: Record<string, number>;
  application_series: { day: string; count: number }[];
  organizations: Record<string, Record<string, number>>;
  timezone: string;
};
export function ReportsPage() {
  const [from, setFrom] = useState(
    new Date(Date.now() - 29 * 86400000).toLocaleDateString("sv-SE", {
      timeZone: "Asia/Tokyo",
    }),
  );
  const [to, setTo] = useState(today());
  const [params, setParams] = useState({ from, to });
  const result = useData<Report>("platform/reports", params);
  const maximum = Math.max(
    1,
    ...(result.data?.application_series.map((d) => d.count) ?? []),
  );
  return (
    <Frame
      title="集計レポート"
      description="個人情報を含まない全体集計です。期間は日本時間で指定します。"
    >
      <Panel>
        <form
          className="flex gap-3 flex-wrap items-end"
          onSubmit={(e) => {
            e.preventDefault();
            setParams({ from, to });
          }}
        >
          <label>
            開始
            <Input
              required
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label>
            終了
            <Input
              required
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <Button>集計</Button>
        </form>
        <LoadState {...result} />
      </Panel>
      {result.data && (
        <>
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(statuses).map(([key, label]) => (
              <Panel key={key} title={label}>
                <p className="text-3xl font-semibold">
                  {result.data.applications[key] ?? 0}
                </p>
              </Panel>
            ))}
          </div>
          <Panel title="日別応募数">
            {result.data.application_series.length ? (
              <div className="flex items-end gap-2 h-52 overflow-x-auto">
                {result.data.application_series.map((d) => (
                  <div
                    className="min-w-12 flex flex-col items-center justify-end h-full"
                    key={d.day}
                  >
                    <span className="text-xs">{d.count}</span>
                    <div
                      className="bg-primary rounded-t w-8"
                      style={{ height: (d.count / maximum) * 150 + "px" }}
                      title={d.day + " · " + d.count + "件"}
                    />
                    <span className="text-xs mt-2">{d.day.slice(5)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p>この期間の応募はありません。</p>
            )}
          </Panel>
          <Panel title="組織の利用状態">
            <div className="grid sm:grid-cols-2 gap-4">
              {Object.entries(result.data.organizations).map(
                ([key, counts]) => (
                  <div key={key}>
                    <h3 className="font-semibold">
                      {key === "agents" ? "紹介会社" : "企業"}
                    </h3>
                    {Object.entries(counts).map(([s, n]) => (
                      <p key={s}>
                        {s} · {n}件
                      </p>
                    ))}
                  </div>
                ),
              )}
            </div>
          </Panel>
        </>
      )}
    </Frame>
  );
}
type Account = {
  id: number;
  name: string;
  email: string;
  platform_role: string | null;
  status: string;
};
export function PlatformUsersPage() {
  const { can } = useAdminSession();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const result = useData<Page<Account>>("platform/users", { page, search });
  const op = useMutation();
  const [edit, setEdit] = useState<Account | null>(null);
  const [reason, setReason] = useState("");
  const [email, setEmail] = useState("");
  if (!can("platform.roles.manage"))
    return (
      <Frame title="ユーザー管理">
        <p>ユーザー管理の権限がありません。</p>
      </Frame>
    );
  return (
    <Frame
      title="プラットフォームユーザー"
      description="OOAK共通アカウントのJob利用権限を管理します。"
    >
      <Feedback {...op} />
      <Panel title="アカウントに権限を付与">
        <form
          className="flex gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            await op.run(async () => {
              const r = await ApiService.get("platform/users/lookup", {
                params: { email },
              });
              setEdit({
                ...r.data.data,
                platform_role: null,
                status: "active",
              });
              setReason("");
            }, "アカウントを確認しました。ロールを選択してください。");
          }}
        >
          <Input
            required
            type="email"
            aria-label="OOAKアカウントのメールアドレス"
            placeholder="OOAKアカウントのメールアドレス"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button disabled={op.busy}>確認</Button>
        </form>
      </Panel>
      {edit && (
        <Panel title="Job利用権限の変更">
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              if (
                await op.run(async () => {
                  await ApiService.patch(
                    "platform/users/" + edit.id + "/roles",
                    {
                      role_key: edit.platform_role,
                      status: edit.status,
                      reason,
                    },
                  );
                  result.reload();
                })
              )
                setEdit(null);
            }}
          >
            <p>
              {edit.name} · {edit.email}
            </p>
            <label className="block">
              プラットフォームロール
              <select
                className={fieldClass}
                value={edit.platform_role ?? ""}
                onChange={(e) =>
                  setEdit({ ...edit, platform_role: e.target.value || null })
                }
              >
                <option value="">プラットフォーム権限なし</option>
                {["platform_admin", "platform_support", "platform_auditor"].map(
                  (r) => (
                    <option value={r} key={r}>
                      {roles[r]}
                    </option>
                  ),
                )}
              </select>
            </label>
            <label className="block">
              Job利用状態
              <select
                className={fieldClass}
                value={edit.status}
                onChange={(e) => setEdit({ ...edit, status: e.target.value })}
              >
                <option value="active">有効</option>
                <option value="suspended">停止</option>
              </select>
            </label>
            <label className="block">
              変更理由
              <Input
                required
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <div className="flex gap-3">
              <Button disabled={op.busy}>保存</Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEdit(null)}
              >
                キャンセル
              </Button>
            </div>
          </form>
        </Panel>
      )}
      <Panel>
        <form
          className="flex gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(draft);
            setPage(1);
          }}
        >
          <Input
            aria-label="名前・メールで検索"
            placeholder="名前・メールで検索"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <Button>検索</Button>
        </form>
        <LoadState {...result} />
        {result.data?.data.map((u) => (
          <div
            key={u.id}
            className="flex flex-wrap gap-3 justify-between py-3 border-b"
          >
            <div>
              <p className="font-semibold">{u.name}</p>
              <p className="text-sm">
                {u.email} · {roles[u.platform_role ?? ""] ?? "組織スタッフ"} ·{" "}
                {u.status}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setEdit(u);
                setReason("");
              }}
            >
              変更
            </Button>
          </div>
        ))}
        {result.data && <Pager result={result.data} setPage={setPage} />}
      </Panel>
    </Frame>
  );
}
type Master = {
  id: number;
  name?: string;
  description?: string;
  is_active: boolean;
  active?: boolean;
  parent?: number;
  publish?: boolean;
  job_type_id?: number;
  tag_group_id?: number;
};
const masters: Record<string, string> = {
  "job-categories": "職種カテゴリ",
  "employment-types": "雇用形態",
  benefits: "福利厚生",
  tags: "求人タグ",
  "tag-groups": "タググループ",
  "pr-tags": "PRタグ",
  "unpublished-reasons": "非公開理由",
  "job-types": "求人区分",
};
export function MastersPage() {
  const { can } = useAdminSession();
  const [kind, setKind] = useState("job-categories");
  const result = useData<Master[]>("platform/masters/" + kind);
  const groups = useData<Master[]>("platform/masters/tag-groups");
  const types = useData<Master[]>("platform/masters/job-types");
  const op = useMutation();
  const [edit, setEdit] = useState<Master | null>(null);
  const start = () =>
    setEdit({
      id: 0,
      name: "",
      description: "",
      is_active: true,
      active: true,
      parent: 0,
      publish: true,
      tag_group_id: 0,
      job_type_id: null,
    });
  return (
    <Frame
      title="マスタ管理"
      description="使用中の値は削除せず、無効にして新規選択を止めます。"
    >
      <Feedback {...op} />
      <Panel>
        <label>
          種類
          <select
            className={fieldClass}
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setEdit(null);
            }}
          >
            {Object.entries(masters).map(([key, label]) => (
              <option value={key} key={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {can("masters.manage") && <Button onClick={start}>追加</Button>}
        <LoadState {...result} />
        {result.data?.map((m) => (
          <div
            className="border-b py-3 flex justify-between items-center gap-3"
            key={m.id}
          >
            <div>
              {m.name ?? m.description}
              <span className="text-sm text-muted-foreground ml-2">
                #{m.id} · {m.is_active ? "有効" : "無効"}
              </span>
            </div>
            {can("masters.manage") && (
              <Button
                variant="outline"
                onClick={() =>
                  setEdit({
                    ...m,
                    is_active: !!m.is_active,
                    publish: !!m.publish,
                  })
                }
              >
                編集
              </Button>
            )}
          </div>
        ))}
      </Panel>
      {edit && (
        <Panel title={edit.id ? "値を編集" : "値を追加"}>
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              if (
                await op.run(async () => {
                  const body: Record<string, unknown> = {
                    is_active: edit.is_active,
                    [kind === "unpublished-reasons" ? "description" : "name"]:
                      kind === "unpublished-reasons"
                        ? edit.description
                        : edit.name,
                  };
                  if (kind === "job-categories")
                    Object.assign(body, {
                      parent: Number(edit.parent ?? 0),
                      publish: edit.publish,
                      job_type_id: edit.job_type_id || null,
                    });
                  if (kind === "tags")
                    body.tag_group_id = Number(edit.tag_group_id);
                  if (
                    [
                      "tag-groups",
                      "pr-tags",
                      "job-types",
                      "employment-types",
                    ].includes(kind)
                  )
                    body.active = edit.is_active;
                  if (edit.id)
                    await ApiService.patch(
                      "platform/masters/" + kind + "/" + edit.id,
                      body,
                    );
                  else await ApiService.post("platform/masters/" + kind, body);
                  result.reload();
                  groups.reload();
                  types.reload();
                })
              )
                setEdit(null);
            }}
          >
            <label className="block">
              名称 / 説明
              <Input
                required
                value={
                  kind === "unpublished-reasons" ? edit.description : edit.name
                }
                onChange={(e) =>
                  setEdit({
                    ...edit,
                    [kind === "unpublished-reasons" ? "description" : "name"]:
                      e.target.value,
                  })
                }
              />
            </label>
            {kind === "job-categories" && (
              <>
                <label className="block">
                  親カテゴリ
                  <select
                    className={fieldClass}
                    value={edit.parent ?? 0}
                    onChange={(e) =>
                      setEdit({ ...edit, parent: Number(e.target.value) })
                    }
                  >
                    <option value={0}>親カテゴリなし</option>
                    {result.data
                      ?.filter((m) => m.id !== edit.id)
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="block">
                  求人区分
                  <select
                    className={fieldClass}
                    value={edit.job_type_id ?? ""}
                    onChange={(e) =>
                      setEdit({
                        ...edit,
                        job_type_id: Number(e.target.value) || null,
                      })
                    }
                  >
                    <option value="">指定なし</option>
                    {types.data?.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex gap-3">
                  <input
                    type="checkbox"
                    checked={edit.publish}
                    onChange={(e) =>
                      setEdit({ ...edit, publish: e.target.checked })
                    }
                  />
                  公開
                </label>
              </>
            )}
            {kind === "tags" && (
              <label className="block">
                タググループ
                <select
                  required
                  className={fieldClass}
                  value={edit.tag_group_id ?? 0}
                  onChange={(e) =>
                    setEdit({ ...edit, tag_group_id: Number(e.target.value) })
                  }
                >
                  <option value={0}>選択してください</option>
                  {groups.data?.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="flex gap-3">
              <input
                type="checkbox"
                checked={edit.is_active}
                onChange={(e) =>
                  setEdit({ ...edit, is_active: e.target.checked })
                }
              />
              有効
            </label>
            <div className="flex gap-3">
              <Button disabled={op.busy}>保存</Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEdit(null)}
              >
                キャンセル
              </Button>
            </div>
          </form>
        </Panel>
      )}
    </Frame>
  );
}
type Delivery = {
  id: number;
  recipient: string;
  status: string;
  attempts: number;
  last_error?: string;
  created_at: string;
  sent_at: string;
};
export function DeliveriesPage() {
  const [page, setPage] = useState(1);
  const result = useData<Page<Delivery>>("platform/deliveries", { page });
  const op = useMutation();
  return (
    <Frame title="メール配信状況">
      <Feedback {...op} />
      <Panel>
        <LoadState {...result} />
        <div className="overflow-x-auto">
          <table className="text-sm w-full text-left">
            <thead>
              <tr>
                {["宛先", "状態", "試行 / 日時", "操作"].map((h) => (
                  <th className={cell} key={h}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.data?.data.map((d) => (
                <tr key={d.id}>
                  <td className={cell}>{d.recipient}</td>
                  <td className={cell}>
                    {d.status}
                    <p>{d.last_error}</p>
                  </td>
                  <td className={cell}>
                    {d.attempts}回 · {japanDate(d.sent_at ?? d.created_at)}
                  </td>
                  <td className={cell}>
                    {d.status !== "sent" && (
                      <Button
                        disabled={op.busy}
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          op.run(async () => {
                            await ApiService.post(
                              "platform/deliveries/" + d.id + "/retry",
                              {},
                            );
                            result.reload();
                          }, "再送信を予約しました。")
                        }
                      >
                        再試行
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {result.data && !result.data.data.length && (
          <p>配信履歴はまだありません。</p>
        )}
        {result.data && <Pager result={result.data} setPage={setPage} />}
      </Panel>
    </Frame>
  );
}
type Application = {
  id: number;
  job_id: number;
  title: string;
  status: string;
  owner_agent_id: number;
  company_id: number;
  created_at: string;
};
export function PlatformApplicationsPage() {
  const { can, session } = useAdminSession();
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const result = useData<Page<Application>>("platform/applications", {
    page,
    search,
    status: status || undefined,
  });
  return (
    <Frame
      title="全応募一覧"
      description="全組織の応募状況を確認できます。個人情報の詳細閲覧には管理対象の選択と本人確認が必要です。"
    >
      <Panel>
        <form
          className="flex flex-wrap gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(draft);
            setPage(1);
          }}
        >
          <Input
            className="sm:w-72"
            placeholder="求人名で検索"
            aria-label="求人名"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <select
            className={fieldClass + " sm:w-52"}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">すべての状態</option>
            {Object.entries(statuses).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <Button>検索</Button>
        </form>
        <LoadState {...result} />
        {result.data?.data.map((a) => (
          <div
            className="border-b py-4 flex flex-wrap gap-3 justify-between"
            key={a.id}
          >
            <div>
              <p className="font-semibold">{a.title}</p>
              <p className="text-sm">
                応募 #{a.id} · {statuses[a.status]} · {japanDate(a.created_at)}
              </p>
            </div>
            {can("applications.read") && (
              <Button
                variant="outline"
                onClick={() =>
                  openTenant(
                    {
                      type: a.owner_agent_id ? "agent" : "company",
                      id: Number(a.owner_agent_id || a.company_id),
                      name: "組織 #" + (a.owner_agent_id || a.company_id),
                      actorId: session.user.id,
                    },
                    "/job-applications/" + a.id,
                  )
                }
              >
                詳細
              </Button>
            )}
          </div>
        ))}
        {result.data && <Pager result={result.data} setPage={setPage} />}
      </Panel>
    </Frame>
  );
}
